"use client";

import { useEffect, useRef, useState } from "react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/shared/components/ui/combobox";
import { comboboxClearKeyDown, type BaseFieldProps, type Option } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

interface FieldComboboxProps extends BaseFieldProps {
  options?: Option[];
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
}

export function FieldCombobox({
  name,
  label,
  description,
  className,
  labelType = "text",
  tooltipContent,
  options = [],
  placeholder,
  emptyMessage = "Sonuc bulunamadi.",
  disabled,
}: FieldComboboxProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const container = rootRef.current?.closest(
      "[data-slot='dialog-content'], [data-slot='sheet-content']",
    ) as HTMLElement | null;
    setPortalContainer(container);
  }, []);

  return (
    <FormField
      name={name}
      render={({ field }) => {
        const selectedOption = options.find((option) => option.value === field.value) ?? null;

        return (
          <FormItem className={className}>
            <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
            <FormControl>
              <div ref={rootRef} className="w-full min-w-0 max-w-full">
                <Combobox
                  items={options}
                  value={selectedOption}
                  itemToStringLabel={(item: Option) => item.label}
                  itemToStringValue={(item: Option) => item.value}
                  isItemEqualToValue={(item: Option, selected: Option) => item.value === selected.value}
                  onValueChange={(nextValue) => {
                    field.onChange(nextValue?.value ?? "");
                  }}
                  modal
                  autoHighlight
                >
                  <ComboboxInput
                    placeholder={placeholder}
                    disabled={disabled}
                    showClear={Boolean(field.value)}
                    className="w-full"
                    onKeyDown={comboboxClearKeyDown(Boolean(field.value), () =>
                      field.onChange(""),
                    )}
                  />
                  {/* Base UI, `container={null}`'ı "kap henüz çözülmedi" sayıp portalı hiç
                      basmaz; body'ye düşmesi için undefined geçilmeli (dialog dışı sayfalarda
                      liste hiç açılmıyordu). */}
                  <ComboboxContent container={portalContainer ?? undefined}>
                    <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
                    <ComboboxList>
                      {/* Group'a `items` verilirse Collection kök filtreyi (filteredItems)
                          yok sayıp ham listeyi basar — yazarak arama çalışmaz. */}
                      <ComboboxCollection>
                        {(item: Option) => (
                          <ComboboxItem key={item.value} value={item}>
                            {item.label}
                          </ComboboxItem>
                        )}
                      </ComboboxCollection>
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
